import React, { FC, ReactNode, Ref, useEffect, useRef } from "react";
import Spinner from "react-spinkit";
import { IndicatorOptions } from "../types/scanview.types";
import { useGlobalContext } from "./ScanViewerContext";
import successPng from "../assets/success.png";
import errorPng from "../assets/error.png";
import frontCard from "../assets/front_card.png";
import backCard from "../assets/back_card.png";
import ScanSwitch from "./scanSwitch";
import MobilePortal from "../components/MobilePortal/MobilePortal";
import styles from "./scanFrame.module.scss";
import { WebToMobileHandOffConfig } from "../components/ErrorModal/components/WebToMobile/WebToMobile";

type Props = {
  successIndicator?: ReactNode;
  errorIndicator?: ReactNode;
  handleRetryButton: () => void;
  frameDivRef: Ref<HTMLDivElement>;
  spinnerOptions: IndicatorOptions;
  webToMobileHandoffConfig?: WebToMobileHandOffConfig;
  cardScanApi?: any;
  backsideSupport?: boolean;
};

const ScanFrame: FC<Props> = ({
  successIndicator,
  handleRetryButton,
  frameDivRef,
  spinnerOptions,
  errorIndicator,
  webToMobileHandoffConfig,
}) => {
  const {
    stateMachineState,
    showCurtain,
    isMobile,
    isMobileOnLandscape,
    guideColor,
    scanningGuideMessage,
  } = useGlobalContext();

  const helperTextRef = useRef<HTMLDivElement>(null);

  const shouldShowProgress = [
    "frontsideProcessing",
    "backsideProcessing",
    "autoCapture",
    "manualCapture",
    "backsidePending",
  ].some(stateMachineState.matches);

  const shouldShowRotateToScanBack =
    stateMachineState.matches("backsidePending") ||
    (stateMachineState.matches("backsideProcessing") &&
      !stateMachineState.context.processing);

  const isProcessing =
    ["frontsideProcessing", "backsideProcessing"].some(
      stateMachineState.matches
    ) && stateMachineState.context.processing;

  const isErrorOrRetry = [
    "error",
    "cameraError",
    "retry",
    "eligibilityError",
  ].some(stateMachineState.matches);

  const greyCorner =
    !stateMachineState.context.scannerRunning &&
    !stateMachineState.context.autoScan;

  useEffect(() => {
    if (
      ["frontsideProcessing", "backsideProcessing"].some(
        stateMachineState.matches
      )
    ) {
      helperTextRef.current?.focus();
    }
  }, [stateMachineState.value]);

  return (
    <>
      <div
        data-cy="cardscanCardGuide"
        className={`${styles.cardGuide} ${
          shouldShowRotateToScanBack ? styles.rotateImage : ""
        } ${isMobile ? styles.mobile : ""} active ${
          stateMachineState.matches("manualCapture")
            ? "cardscan-card-guide-processing"
            : ""
        } ${isMobileOnLandscape && "card-guide-landscape"} ${
          stateMachineState.matches("success") ? styles.scanCompleted : ""
        }`}
        ref={frameDivRef}
      >
        {!showCurtain && !stateMachineState.context.processing && (
          <ScanSwitch />
        )}

        {shouldShowProgress && (
          <>
            <div
              className={`
                ${styles.corner} 
                ${styles.corner1} 
                ${
                  guideColor === "notDetected"
                    ? styles.notDetectedGuideColor
                    : styles.detectedGuideColor
                }
                ${greyCorner ? styles.cardscanCornerOff : ""}
                ${shouldShowProgress ? "cardscan-corner-active" : ""}
                ${isProcessing ? styles.animated : ""}
              `}
            />
            <div
              className={`
                ${styles.corner} 
                ${styles.corner2}
                ${
                  guideColor === "notDetected"
                    ? styles.notDetectedGuideColor
                    : styles.detectedGuideColor
                }
                ${greyCorner ? styles.cardscanCornerOff : ""}
                ${shouldShowProgress ? "cardscan-corner-active" : ""}
                ${isProcessing ? styles.animated : ""}
              `}
            />
            <div
              className={`
                ${styles.corner} 
                ${styles.corner3}
                ${
                  guideColor === "notDetected"
                    ? styles.notDetectedGuideColor
                    : styles.detectedGuideColor
                }
                ${greyCorner ? styles.cardscanCornerOff : ""}
                ${shouldShowProgress ? "cardscan-corner-active" : ""}
                ${isProcessing ? styles.animated : ""}
              `}
            />
            <div
              className={`
                ${styles.corner}  
                ${styles.corner4}
                ${
                  guideColor === "notDetected"
                    ? styles.notDetectedGuideColor
                    : styles.detectedGuideColor
                }
                ${greyCorner ? styles.cardscanCornerOff : ""}
                ${shouldShowProgress ? "cardscan-corner-active" : ""}
                ${isProcessing ? styles.animated : ""}
              `}
            />
          </>
        )}

        {stateMachineState.matches("success") &&
          (successIndicator ? (
            successIndicator
          ) : (
            <img
              src={successPng}
              className={`${isMobile ? styles.mobile : ""} ${
                isMobileOnLandscape ? styles.mobileLandscape : ""
              }`}
              alt="checkmark indicating successful scan"
              draggable={false}
            />
          ))}

        {isErrorOrRetry &&
          (!webToMobileHandoffConfig?.enabled || isMobile) &&
          (errorIndicator ? (
            errorIndicator
          ) : (
            <img
              src={errorPng}
              className={`${styles.mobile}`}
              alt="warning triangle with exclamation point indicating an error"
              draggable={false}
            />
          ))}

        {(stateMachineState.matches("eligibilityProcessing") ||
          stateMachineState.matches("postProcessing")) && (
          <div
            data-cy="cardScanCameraLoading"
            className={styles.spinnerWrapper}
          >
            <Spinner {...spinnerOptions} />
          </div>
        )}

        {shouldShowRotateToScanBack && (
          <div
            className={`${styles.rotateImage} ${isMobile ? styles.mobile : ""}`}
          >
            <div
              className={`${styles.rotateImageContainer} ${
                isMobile ? styles.mobile : ""
              }`}
            >
              <img
                src={frontCard}
                className={styles.frontCard}
                style={{ maxWidth: "100px" }}
                draggable={false}
                alt="Front side of card"
              />
              <img
                src={backCard}
                className={styles.backCard}
                style={{ maxWidth: "100px" }}
                draggable={false}
                alt="Back side of card"
              />
            </div>
          </div>
        )}

        <MobilePortal elementId="mobileTopCurtain">
          <div
            aria-live="assertive"
            aria-atomic="true"
            role="alert"
            ref={helperTextRef}
            tabIndex={-1}
          >
            <p
              data-cy="cardscanHelperText"
              id={styles.cardscanHelperText}
              className={`${isMobile ? styles.mobile : ""} ${
                isMobileOnLandscape ? styles.mobileLandscape : ""
              }`}
            >
              {stateMachineState.context.headerTitle}
            </p>
            <p
              id={styles.cardscanHelperText}
              className={`${styles.detectionHelperText} ${
                scanningGuideMessage && isProcessing ? styles.show : ""
              } ${isMobile ? styles.mobile : ""} ${
                isMobileOnLandscape ? styles.mobileLandscape : ""
              }`}
            >
              {scanningGuideMessage}
            </p>
          </div>
        </MobilePortal>
        {stateMachineState.matches("retry") && (
          <button
            className={`${styles.cardscanRetryButton} retryScanBtn`}
            onClick={handleRetryButton}
            aria-label="Retry scan"
          >
            Retry Scan
          </button>
        )}
      </div>
    </>
  );
};

export default ScanFrame;
